import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ApiClient } from '../api'
import type { ChangeNotice, ChangeNoticeBody, CnSignStatus, CnFeedKind, CnAuthorRole } from '../types'

// useChangeNotice — the data layer for a forecast's formal change notice
// (mig 077). Separate React Query key from the forecast payload; every mutation
// returns the full notice (notice + signoff[] + feed[]) so the cache stays whole.

export function useChangeNotice(eventId: number) {
  const qc = useQueryClient()
  const enabled = Number.isFinite(eventId) && eventId > 0
  const key = ['change-notice', eventId]

  const q = useQuery({
    queryKey: key,
    enabled,
    queryFn: async (): Promise<ChangeNotice | null> => (await ApiClient.changeNotice(eventId)).notice
  })
  const set = (n: ChangeNotice) => qc.setQueryData(key, n)

  const saveMut = useMutation({ mutationFn: (body: ChangeNoticeBody) => ApiClient.putChangeNotice(eventId, body), onSuccess: (r) => set(r.notice) })
  const addSignoffMut = useMutation({ mutationFn: (body: { role: string; name?: string | null; personId?: number | null }) => ApiClient.addCnSignoff(eventId, body), onSuccess: (r) => set(r.notice) })
  const patchSignoffMut = useMutation({ mutationFn: ({ rowId, status }: { rowId: number; status: CnSignStatus }) => ApiClient.patchCnSignoff(rowId, { status }), onSuccess: (r) => set(r.notice) })
  const removeSignoffMut = useMutation({ mutationFn: (rowId: number) => ApiClient.deleteCnSignoff(rowId), onSuccess: () => qc.invalidateQueries({ queryKey: key }) })
  const addFeedMut = useMutation({ mutationFn: (body: { kind: CnFeedKind; authorRole: CnAuthorRole; body: string }) => ApiClient.addCnFeed(eventId, body), onSuccess: (r) => set(r.notice) })

  return {
    notice: q.data ?? null,
    isLoading: q.isLoading,
    save: (body: ChangeNoticeBody) => saveMut.mutateAsync(body),
    saving: saveMut.isPending,
    addSignoff: (body: { role: string; name?: string | null; personId?: number | null }) => addSignoffMut.mutateAsync(body),
    patchSignoff: (rowId: number, status: CnSignStatus) => patchSignoffMut.mutateAsync({ rowId, status }),
    removeSignoff: (rowId: number) => removeSignoffMut.mutateAsync(rowId),
    addFeed: (body: { kind: CnFeedKind; authorRole: CnAuthorRole; body: string }) => addFeedMut.mutateAsync(body)
  }
}
